import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableCustomComponentSlotProps,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {RowMetadata, TableColumnType} from 'notebook/tables/TableConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

import StoichiometryTableEmbeddedChemicalSketcher from './StoichiometryTableEmbeddedChemicalSketcher';

export interface StoichiometryStructuredTableRowMetadata extends RowMetadata {}

export interface StoichiometryStructuredTableMetadata {}

export type StoichiometryStructuredTable = StructuredTable<
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata
>;

export default class StoichiometryStructuredTableAdapter extends GenericStructuredTableAdapter<
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata
> {
  getBlankRowMetadata(
    tableMetadata: StoichiometryStructuredTableMetadata
  ): StoichiometryStructuredTableRowMetadata {
    return {};
  }

  override getDisplayName(structuredTable: StoichiometryStructuredTable): string {
    return 'Reaction';
  }

  override allowSingleInputs(_: StoichiometryStructuredTable): boolean {
    return false;
  }

  override injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: StoichiometryStructuredTableMetadata;
  }): StructuredTableColumn {
    if (column.columnType === TableColumnType.EXISTING_BIOENTITY) {
      return {
        ...column,
        frontEndMetadata: {
          validSchemas: 'ALL',
        },
      };
    }
    return column;
  }

  override getInitialColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string | null;
    metadata: StoichiometryStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return [
      {
        ...makeStructuredTableColumn({columnType: TableColumnType.EXISTING_BIOENTITY, name: 'Name'}),
        columnType: TableColumnType.EXISTING_BIOENTITY,
        bioentityTypeFilters: [ModelTypeConstants.MOLECULE],
      },
      {
        ...makeStructuredTableColumn({columnType: TableColumnType.OPTION_SELECTOR, name: 'Reaction Role'}),
        columnType: TableColumnType.OPTION_SELECTOR,
        options: ['Reactant', 'Product'],
      },
      {
        ...makeStructuredTableColumn({columnType: TableColumnType.CUSTOM, name: 'Coefficient'}),
        columnType: TableColumnType.CUSTOM,
        customColumnConfig: {dataType: CustomColumnDataType.FLOAT},
      },
      {
        ...makeStructuredTableColumn({columnType: TableColumnType.CUSTOM, name: 'Mols'}),
        columnType: TableColumnType.CUSTOM,
        customColumnConfig: {dataType: CustomColumnDataType.FLOAT},
      },
    ];
  }

  override isPositionReadOnly(
    currentColumnIndex: number,
    row: StructuredTableRow<StoichiometryStructuredTableRowMetadata>,
    structuredTable: StoichiometryStructuredTable
  ): boolean {
    return super.isPositionReadOnly(currentColumnIndex, row, structuredTable);
  }

  override experimental_renderAboveTableCustomComponent(
    props: StructuredTableCustomComponentSlotProps<
      StoichiometryStructuredTableMetadata,
      StoichiometryStructuredTableRowMetadata
    >
  ): React.ReactNode {
    return <StoichiometryTableEmbeddedChemicalSketcher {...props} />;
  }
}
